import { useEffect } from 'react';
import { Clock3, Droplets, Phone, ShieldCheck, Star, Wrench } from 'lucide-react';

const services = [
  ['Drain Cleaning', 'Fast clearing for sinks, tubs, and main lines.', Droplets],
  ['Leak Repair', 'Accurate detection and durable repairs without guesswork.', Wrench],
  ['Water Heaters', 'Repair and installation for tank and tankless systems.', ShieldCheck],
] as const;

const reviews = [
  ['James W.', 'Clear quote, tidy work, and hot water the same afternoon.'],
  ['Emily R.', 'They arrived within an hour and handled our burst pipe calmly.'],
  ['Carlos M.', 'Excellent communication, fair pricing, and spotless work.'],
] as const;

export default function ShowcaseSitePreview() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('foundlioSession');
    const requestedParentOrigin = params.get('foundlioParentOrigin');
    const parentOrigin = requestedParentOrigin === window.location.origin
      ? requestedParentOrigin
      : null;
    const protocol = 'foundlio-preview/v1';

    const post = (type: string, payload: Record<string, unknown> = {}) => {
      if (!parentOrigin || !sessionId) return;
      window.parent.postMessage({
        source: 'foundlio-preview',
        protocol,
        sessionId,
        type,
        payload,
      }, parentOrigin);
    };

    const onMessage = (event: MessageEvent) => {
      const message = event.data;
      if (
        event.source !== window.parent ||
        event.origin !== parentOrigin ||
        !message ||
        message.source !== 'foundlio-portal' ||
        message.protocol !== protocol ||
        message.sessionId !== sessionId
      ) return;
      const payload = message.payload || {};

      if (message.type === 'apply-draft-patches') {
        payload.patches?.slice(0, 200).forEach((patch: { key: string; value: string; kind?: string }) => {
          if (!/^[a-zA-Z0-9._-]{1,160}$/.test(patch.key) || typeof patch.value !== 'string') return;
          const attr = patch.kind === 'src' ? 'data-edit-src' : patch.kind === 'href' ? 'data-edit-href' : 'data-edit';
          document.querySelectorAll<HTMLElement>(`[${attr}="${CSS.escape(patch.key)}"]`).forEach((element) => {
            if (patch.kind === 'src' && element instanceof HTMLImageElement) element.src = patch.value;
            else if (patch.kind === 'href' && element instanceof HTMLAnchorElement) element.href = patch.value;
            else element.textContent = patch.value;
          });
        });
      }

      if (message.type === 'scroll-to-element' && typeof payload.key === 'string') {
        document.querySelector(`[data-edit-section="${CSS.escape(payload.key)}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      if (message.type === 'clear-selection') {
        document.querySelectorAll('[data-showcase-selected="1"]')
          .forEach((element) => element.removeAttribute('data-showcase-selected'));
      }
    };

    const style = document.createElement('style');
    style.textContent = `
      [data-edit], [data-edit-src], [data-edit-href], [data-edit-section] { cursor: pointer; }
      [data-showcase-hover="1"] { outline: 1px solid rgba(14, 116, 144, .7); outline-offset: 2px; }
      [data-showcase-selected="1"] { outline: 2px solid rgb(8, 145, 178); outline-offset: 3px; }
    `;
    document.head.appendChild(style);

    const getDescriptor = (element: HTMLElement) => {
      const key = element.dataset.edit || element.dataset.editSrc || element.dataset.editHref;
      if (!key) return null;
      const kind = element.dataset.editSrc ? 'src' : element.dataset.editHref ? 'href' : 'text';
      const rect = element.getBoundingClientRect();
      const section = element.closest<HTMLElement>('[data-edit-section]')?.dataset.editSection;
      return {
        key,
        kind,
        label: key.split('.').filter((part) => !/^\d+$/.test(part)).slice(-2).join(' '),
        section,
        rect: {
          top: rect.top,
          left: rect.left,
          right: rect.right,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height,
        },
      };
    };

    const onPointerOver = (event: PointerEvent) => {
      const element = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-edit], [data-edit-src], [data-edit-href]')
        : null;
      if (!element) return;
      element.dataset.showcaseHover = '1';
      const descriptor = getDescriptor(element);
      if (descriptor) post('element-hover', { element: descriptor });
    };

    const onPointerOut = (event: PointerEvent) => {
      const element = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-edit], [data-edit-src], [data-edit-href]')
        : null;
      element?.removeAttribute('data-showcase-hover');
      post('element-hover', { element: null });
    };

    const onClick = (event: MouseEvent) => {
      const element = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-edit], [data-edit-src], [data-edit-href]')
        : null;
      if (element) {
        event.preventDefault();
        event.stopPropagation();
        document.querySelectorAll('[data-showcase-selected="1"]')
          .forEach((candidate) => candidate.removeAttribute('data-showcase-selected'));
        element.dataset.showcaseSelected = '1';
        const descriptor = getDescriptor(element);
        if (descriptor) {
          post('element-selected', { element: descriptor });
          if (descriptor.kind === 'text') post('edit-requested', { element: descriptor });
        }
        return;
      }
      const section = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-edit-section]')?.dataset.editSection
        : null;
      if (section) post('section-selected', { key: section });
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const element = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-edit], [data-edit-src], [data-edit-href]')
        : null;
      if (!element) return;
      event.preventDefault();
      element.click();
    };

    const editableElements = Array.from(document.querySelectorAll<HTMLElement>('[data-edit], [data-edit-src], [data-edit-href]'));
    editableElements.forEach((element) => {
      if (!element.hasAttribute('tabindex')) element.tabIndex = 0;
    });

    window.addEventListener('message', onMessage);
    document.addEventListener('pointerover', onPointerOver, true);
    document.addEventListener('pointerout', onPointerOut, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeyDown, true);
    const readyTimer = window.setTimeout(() => post('ready', {
      capabilities: ['selection', 'overlay-edit', 'v1'],
    }), 100);
    return () => {
      window.clearTimeout(readyTimer);
      window.removeEventListener('message', onMessage);
      document.removeEventListener('pointerover', onPointerOver, true);
      document.removeEventListener('pointerout', onPointerOut, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKeyDown, true);
      style.remove();
    };
  }, []);

  return (
    <main className="min-h-screen bg-white text-[#14201d] font-sans">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-black/10 bg-white/95 px-8 py-4 backdrop-blur">
        <div className="flex items-center gap-3 font-bold tracking-normal">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#e8ff66]"><Droplets size={19} /></span>
          <span data-edit="business.name">BrightFlow Plumbing</span>
        </div>
        <div className="flex items-center gap-6 text-sm font-medium">
          <span>Services</span><span>About</span><span>Reviews</span>
          <a data-edit-href="business.phoneTel" href="tel:+16025550147" className="flex items-center gap-2 rounded-md bg-[#14201d] px-4 py-2.5 text-white">
            <Phone size={15} /><span data-edit="business.phone">(602) 555-0147</span>
          </a>
        </div>
      </header>

      <section data-edit-section="hero" className="grid min-h-[570px] grid-cols-[1.05fr_.95fr] bg-[#edf4f0]">
        <div className="flex flex-col justify-center px-12 py-16">
          <div className="mb-5 flex w-fit items-center gap-2 rounded-md bg-white px-3 py-2 text-xs font-semibold uppercase text-[#466359]">
            <Clock3 size={14} /> Same-day service across Phoenix
          </div>
          <h1 className="max-w-xl text-6xl font-black leading-[1.02] tracking-normal">
            <span data-edit="hero.headline">Plumbing Done</span>{' '}
            <span data-edit="hero.headlineAccent" className="text-[#177b68]">Right</span>
          </h1>
          <p data-edit="hero.description" className="mt-6 max-w-xl text-lg leading-8 text-[#51615c]">
            Licensed Phoenix plumbers delivering fast, reliable service with honest pricing and clean workmanship.
          </p>
          <div className="mt-8 flex items-center gap-4">
            <button className="rounded-md bg-[#e8ff66] px-6 py-3.5 font-bold text-[#14201d]">Book a plumber</button>
            <span className="flex items-center gap-2 text-sm font-semibold"><Star size={16} fill="#177b68" /> 4.9 from 186 reviews</span>
          </div>
        </div>
        <img data-edit-src="hero.image" src="https://img.foundlio.com/image/upload/c_fill,w_1280,h_1100,q_auto:best,f_auto/v1774093772/foundlio/verticals/hero-plumbing-split.jpg" className="h-full min-h-[570px] w-full object-cover" alt="BrightFlow plumber at work" />
      </section>

      <section data-edit-section="services" className="px-12 py-20">
        <p className="text-sm font-bold uppercase text-[#177b68]">How we can help</p>
        <h2 className="mt-2 text-4xl font-black tracking-normal">Plumbing solutions built to last</h2>
        <div className="mt-10 grid grid-cols-3 gap-5">
          {services.map(([name, description, Icon], index) => (
            <article key={name} className="border border-[#d8e2de] p-6">
              <Icon className="text-[#177b68]" size={24} />
              <h3 data-edit={`services.${index}.name`} className="mt-5 text-xl font-bold">{name}</h3>
              <p data-edit={`services.${index}.shortDescription`} className="mt-3 leading-7 text-[#63716c]">{description}</p>
            </article>
          ))}
        </div>
      </section>

      <section data-edit-section="testimonials" className="bg-[#14201d] px-12 py-20 text-white">
        <h2 className="text-4xl font-black tracking-normal">Trusted by Phoenix homeowners</h2>
        <div className="mt-9 grid grid-cols-3 gap-5">
          {reviews.map(([name, quote], index) => (
            <article key={name} className="border border-white/15 bg-white/5 p-6">
              <div className="flex gap-1 text-[#e8ff66]">{[0, 1, 2, 3, 4].map((star) => <Star key={star} size={15} fill="currentColor" />)}</div>
              <p data-edit={`testimonials.${index}.quote`} className="mt-5 leading-7 text-white/80">{quote}</p>
              <p data-edit={`testimonials.${index}.author`} className="mt-5 font-bold">{name}</p>
            </article>
          ))}
        </div>
      </section>

      <footer data-edit-section="contact" className="flex items-center justify-between px-12 py-10">
        <div><p className="font-bold" data-edit="business.name">BrightFlow Plumbing</p><p data-edit="business.address" className="mt-1 text-sm text-[#63716c]">1820 E Camelback Rd, Phoenix, AZ 85016</p></div>
        <a data-edit-href="business.phoneTel" href="tel:+16025550147" className="font-bold text-[#177b68]"><span data-edit="business.phone">(602) 555-0147</span></a>
      </footer>
    </main>
  );
}
